import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────────────────────────
   TAP AWAY — Legal Pages
   Privacy Policy + Terms & Conditions
   Developer: AlphaY  ·  alphay28071994@gmail.com
   Effective: May 2026
───────────────────────────────────────────────────────────────── */

const EFFECTIVE_DATE = "May 8, 2026";
const APP_NAME = "Tap Away: Arrow Puzzle";
const DEV_NAME = "AlphaY";
const DEV_EMAIL = "alphay28071994@gmail.com";
const PACKAGE = "com.alphay.tapaway";

// ── Global styles injected once ──────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }

:root {
  --bg:        #080C14;
  --bg2:       #0E1420;
  --surface:   #131B28;
  --elevated:  #1A2436;
  --border:    rgba(255,255,255,0.07);
  --border2:   rgba(255,255,255,0.12);
  --accent:    #4CC9F0;
  --accent2:   #A29BFE;
  --accent3:   #FFD60A;
  --green:     #06D6A0;
  --red:       #FF4D6D;
  --orange:    #FF9F1C;
  --text:      #EDF2FF;
  --muted:     #8899BB;
  --faint:     #3D4D6A;
  --radius:    16px;
  --radius-sm: 10px;
}

body {
  background: var(--bg);
  color: var(--text);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  line-height: 1.8;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

/* ── Animated background ── */
.ta-bg {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  background:
    radial-gradient(ellipse 60% 40% at 15% 20%, rgba(76,201,240,0.06) 0%, transparent 60%),
    radial-gradient(ellipse 50% 50% at 85% 75%, rgba(162,155,254,0.07) 0%, transparent 60%),
    radial-gradient(ellipse 80% 60% at 50% 50%, rgba(8,12,20,1) 40%, transparent 100%);
}

.ta-grid {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,0.028) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.028) 1px, transparent 1px);
  background-size: 52px 52px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 30%, black 20%, transparent 80%);
}

/* ── Nav ── */
.ta-nav {
  position: sticky; top: 0; z-index: 100;
  background: rgba(8,12,20,0.85);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--border);
  padding: 0 24px;
}

.ta-nav-inner {
  max-width: 860px; margin: 0 auto;
  display: flex; align-items: center; gap: 16px;
  height: 60px;
}

.ta-nav-logo {
  display: flex; align-items: center; gap: 10px;
  font-weight: 800; font-size: 15px; color: var(--text);
  text-decoration: none; flex-shrink: 0;
}

.ta-nav-icon {
  width: 32px; height: 32px; border-radius: 8px;
  background: linear-gradient(135deg, #4361EE, #7B2FBE);
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; color: white;
}

.ta-nav-tabs {
  display: flex; gap: 4px; margin-left: auto;
}

.ta-tab {
  padding: 6px 16px; border-radius: 100px; font-size: 13px;
  font-weight: 600; cursor: pointer; transition: all 0.2s;
  border: 1px solid transparent; background: transparent;
  color: var(--muted); font-family: 'Plus Jakarta Sans', sans-serif;
}

.ta-tab:hover { color: var(--text); border-color: var(--border2); }

.ta-tab.active {
  background: var(--elevated);
  color: var(--accent);
  border-color: rgba(76,201,240,0.25);
}

/* ── Layout ── */
.ta-page { position: relative; z-index: 1; }

.ta-wrap {
  max-width: 860px; margin: 0 auto;
  padding: 0 24px 100px;
}

/* ── Hero ── */
.ta-hero {
  padding: 72px 0 56px;
  animation: fadeUp 0.7s ease both;
}

.ta-hero-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 5px 14px; border-radius: 100px;
  background: rgba(76,201,240,0.08);
  border: 1px solid rgba(76,201,240,0.2);
  font-size: 11px; font-weight: 700; letter-spacing: 1.6px;
  color: var(--accent); text-transform: uppercase;
  margin-bottom: 24px;
}

.ta-hero h1 {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(36px, 6vw, 58px);
  line-height: 1.06; letter-spacing: -0.5px;
  color: var(--text); margin-bottom: 16px;
}

.ta-hero h1 em {
  font-style: italic; color: var(--accent);
}

.ta-hero-sub {
  color: var(--muted); font-size: 15px; max-width: 520px;
  line-height: 1.7;
}

.ta-hero-meta {
  display: flex; flex-wrap: wrap; gap: 12px 24px;
  margin-top: 24px;
}

.ta-meta-chip {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--faint);
}

.ta-meta-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--accent); opacity: 0.6;
}

/* ── Section cards ── */
.ta-section {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 28px 30px;
  margin-bottom: 12px;
  transition: border-color 0.25s, transform 0.25s;
  animation: fadeUp 0.5s ease both;
}

.ta-section:hover {
  border-color: rgba(76,201,240,0.14);
  transform: translateY(-1px);
}

.ta-section-head {
  display: flex; align-items: center; gap: 14px;
  margin-bottom: 18px;
}

.ta-section-badge {
  width: 40px; height: 40px; min-width: 40px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
}

.ta-section-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  color: var(--text); letter-spacing: -0.2px;
}

.ta-section-num {
  font-size: 11px; font-weight: 600;
  color: var(--faint); letter-spacing: 1px;
  text-transform: uppercase; margin-bottom: 2px;
}

.ta-body { color: var(--muted); font-size: 14px; line-height: 1.85; }
.ta-body + .ta-body { margin-top: 10px; }
.ta-body strong { color: var(--text); font-weight: 600; }
.ta-body a { color: var(--accent); text-decoration: none; border-bottom: 1px solid rgba(76,201,240,0.3); }
.ta-body a:hover { border-color: var(--accent); }

.ta-list {
  list-style: none; padding: 0; margin-top: 10px;
}

.ta-list li {
  color: var(--muted); font-size: 14px; line-height: 1.75;
  padding: 5px 0 5px 24px; position: relative;
}

.ta-list li::before {
  content: ''; position: absolute; left: 0; top: 13px;
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--accent); opacity: 0.5;
}

.ta-list li strong { color: var(--text); font-weight: 600; }

/* ── Callout boxes ── */
.ta-callout {
  border-radius: var(--radius-sm);
  padding: 13px 16px; margin-top: 14px;
  font-size: 13px; line-height: 1.7;
  display: flex; gap: 10px; align-items: flex-start;
}

.ta-callout.green {
  background: rgba(6,214,160,0.07);
  border: 1px solid rgba(6,214,160,0.18);
  color: rgba(6,214,160,0.85);
}

.ta-callout.blue {
  background: rgba(76,201,240,0.06);
  border: 1px solid rgba(76,201,240,0.18);
  color: rgba(76,201,240,0.85);
}

.ta-callout.orange {
  background: rgba(255,159,28,0.07);
  border: 1px solid rgba(255,159,28,0.2);
  color: rgba(255,159,28,0.85);
}

.ta-callout.red {
  background: rgba(255,77,109,0.07);
  border: 1px solid rgba(255,77,109,0.2);
  color: rgba(255,77,109,0.85);
}

.ta-callout-icon { flex-shrink: 0; margin-top: 1px; font-size: 14px; }

/* ── Inline table ── */
.ta-table {
  width: 100%; border-collapse: collapse;
  margin-top: 14px; font-size: 13px;
}

.ta-table th {
  text-align: left; color: var(--faint);
  font-weight: 600; font-size: 11px; letter-spacing: 1px;
  text-transform: uppercase; padding: 8px 12px;
  border-bottom: 1px solid var(--border);
}

.ta-table td {
  padding: 10px 12px; color: var(--muted);
  border-bottom: 1px solid rgba(255,255,255,0.04);
  vertical-align: top;
}

.ta-table td:first-child { color: var(--text); font-weight: 500; }
.ta-table tr:last-child td { border-bottom: none; }

/* ── Divider ── */
.ta-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border2), transparent);
  margin: 40px 0;
}

/* ── Contact card ── */
.ta-contact {
  background: linear-gradient(135deg, rgba(67,97,238,0.12), rgba(123,47,190,0.12));
  border: 1px solid rgba(162,155,254,0.2);
  border-radius: 20px;
  padding: 40px 36px;
  text-align: center;
  margin-top: 40px;
}

.ta-contact h3 {
  font-family: 'Instrument Serif', serif;
  font-size: 26px; color: var(--text); margin-bottom: 10px;
}

.ta-contact p { color: var(--muted); font-size: 14px; margin-bottom: 24px; }

.ta-contact-btn {
  display: inline-flex; align-items: center; gap: 8px;
  background: linear-gradient(90deg, #4361EE, #7B2FBE);
  color: white; font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700; font-size: 14px;
  padding: 13px 28px; border-radius: 100px;
  text-decoration: none; transition: opacity 0.2s, transform 0.2s;
  border: none; cursor: pointer;
}

.ta-contact-btn:hover { opacity: 0.88; transform: translateY(-2px); }

/* ── Footer ── */
.ta-footer {
  text-align: center; padding-top: 48px;
  color: var(--faint); font-size: 12px; line-height: 2;
}

.ta-footer strong { color: var(--muted); }

/* ── Animations ── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .ta-wrap { padding: 0 16px 80px; }
  .ta-hero  { padding: 48px 0 40px; }
  .ta-section { padding: 22px 20px; }
  .ta-nav-tabs .ta-tab { padding: 5px 12px; font-size: 12px; }
  .ta-contact { padding: 28px 20px; }
}
`;

// ── Privacy Policy content ───────────────────────────────────────
const PP_SECTIONS = [
  {
    num: "01", emoji: "📋", color: "rgba(76,201,240,0.1)",
    title: "Overview",
    content: () => <>
      <p className="ta-body">
        <strong>{DEV_NAME}</strong> ("developer", "we", "I") built <strong>{APP_NAME}</strong> as a free
        mobile puzzle game available on Google Play. This Privacy Policy explains how we handle any
        information related to your use of the app.
      </p>
      <p className="ta-body">
        By downloading or using {APP_NAME}, you agree to the terms described in this policy.
        If you disagree with any part, please discontinue use of the app.
      </p>
      <div className="ta-callout green">
        <span className="ta-callout-icon">✅</span>
        <span>
          <strong>Simple summary:</strong> Tap Away does not collect, store, or sell your personal data.
          No account is required to play. All game progress is stored locally on your device only.
        </span>
      </div>
    </>
  },
  {
    num: "02", emoji: "📊", color: "rgba(162,155,254,0.1)",
    title: "Information We Collect",
    content: () => <>
      <p className="ta-body">
        <strong>Tap Away is designed to be privacy-first.</strong> We collect the bare minimum needed
        to provide and improve the game:
      </p>
      <table className="ta-table">
        <thead>
          <tr>
            <th>Data Type</th>
            <th>Collected?</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Name / Email</td><td>❌ No</td><td>No account required</td></tr>
          <tr><td>Location</td><td>❌ No</td><td>Not requested</td></tr>
          <tr><td>Game progress</td><td>Locally only</td><td>Stored on your device via SharedPreferences</td></tr>
          <tr><td>Device info</td><td>Anonymous</td><td>Crash reporting to fix bugs</td></tr>
          <tr><td>Usage analytics</td><td>Anonymous</td><td>Understand how levels are played</td></tr>
          <tr><td>Payment data</td><td>❌ No</td><td>App is completely free</td></tr>
        </tbody>
      </table>
      <div className="ta-callout blue">
        <span className="ta-callout-icon">ℹ️</span>
        <span>
          Your level completion, stars, and daily challenge streak are stored only on your device.
          Uninstalling the app removes this data permanently.
        </span>
      </div>
    </>
  },
  {
    num: "03", emoji: "🔧", color: "rgba(6,214,160,0.1)",
    title: "Third-Party Services",
    content: () => <>
      <p className="ta-body">
        Tap Away may use the following third-party services that have their own privacy policies.
        We recommend reviewing each policy:
      </p>
      <ul className="ta-list">
        <li>
          <strong>Google Play Services</strong> — for app distribution, updates, and crash reporting.{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Privacy Policy</a>
        </li>
        <li>
          <strong>Firebase Crashlytics (optional)</strong> — anonymous crash reports to help us fix bugs.
          No personally identifiable information is included.
        </li>
        <li>
          <strong>AdMob (if ads are enabled)</strong> — Google's advertising platform may collect
          anonymised device identifiers to serve contextual ads.{" "}
          <a href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noreferrer">AdMob Policy</a>
        </li>
      </ul>
      <div className="ta-callout orange">
        <span className="ta-callout-icon">⚠️</span>
        <span>
          If the app displays ads, Google AdMob may use your advertising ID in accordance with
          Google's privacy policy. You can reset or opt out of personalised ads in your device settings
          under <strong>Google → Ads</strong>.
        </span>
      </div>
    </>
  },
  {
    num: "04", emoji: "🔒", color: "rgba(76,201,240,0.1)",
    title: "Data Security",
    content: () => <>
      <p className="ta-body">
        All game data (progress, stars, settings) is stored locally on your device using Android's
        SharedPreferences system. This data is:
      </p>
      <ul className="ta-list">
        <li>Never transmitted to our servers</li>
        <li>Never shared with third parties by us</li>
        <li>Protected by your device's own security model</li>
        <li>Automatically deleted when you uninstall the app</li>
      </ul>
      <p className="ta-body" style={{ marginTop: 12 }}>
        While we strive to implement commercially acceptable security practices, no method of
        data storage is 100% secure. We cannot guarantee absolute protection against all risks.
      </p>
    </>
  },
  {
    num: "05", emoji: "👶", color: "rgba(255,77,109,0.1)",
    title: "Children's Privacy",
    content: () => <>
      <p className="ta-body">
        Tap Away is a family-friendly puzzle game suitable for all ages. It is rated <strong>Everyone</strong> on
        Google Play and does not knowingly collect personal information from children under 13.
      </p>
      <p className="ta-body">
        The app requires no registration, login, or personal information to play.
        If you are a parent and believe your child has somehow provided personal data to us,
        please contact us immediately at <a href={`mailto:${DEV_EMAIL}`}>{DEV_EMAIL}</a> and
        we will take immediate action.
      </p>
      <div className="ta-callout green">
        <span className="ta-callout-icon">✅</span>
        <span>
          No account required. No personal data collected. Safe for children of all ages.
        </span>
      </div>
    </>
  },
  {
    num: "06", emoji: "🔗", color: "rgba(255,159,28,0.1)",
    title: "Links to Other Sites",
    content: () => <>
      <p className="ta-body">
        The app or this page may contain links to external websites (such as Google Play, support
        pages, or legal documents). We have no control over the content or privacy practices of
        those sites and accept no responsibility for them.
      </p>
      <p className="ta-body">
        We encourage you to review the Privacy Policy of any external site you visit.
      </p>
    </>
  },
  {
    num: "07", emoji: "🔄", color: "rgba(162,155,254,0.1)",
    title: "Changes to This Policy",
    content: () => <>
      <p className="ta-body">
        We may update this Privacy Policy from time to time to reflect changes in the app or
        applicable laws. Updates will be posted at this URL with a revised effective date.
      </p>
      <p className="ta-body">
        Continued use of the app after any changes constitutes your acceptance of the updated policy.
        We encourage you to review this page periodically.
      </p>
      <div className="ta-callout blue">
        <span className="ta-callout-icon">📅</span>
        <span>Current effective date: <strong>{EFFECTIVE_DATE}</strong></span>
      </div>
    </>
  },
];

// ── Terms & Conditions content ───────────────────────────────────
const TC_SECTIONS = [
  {
    num: "01", emoji: "📜", color: "rgba(162,155,254,0.1)",
    title: "Acceptance of Terms",
    content: () => <>
      <p className="ta-body">
        By downloading, installing, or using <strong>{APP_NAME}</strong> (the "App"), you agree to be
        bound by these Terms and Conditions. If you do not agree, do not use the App.
      </p>
      <p className="ta-body">
        These terms apply to all users of the App. We reserve the right to modify these terms
        at any time. Continued use of the App after changes constitutes acceptance.
      </p>
    </>
  },
  {
    num: "02", emoji: "📱", color: "rgba(76,201,240,0.1)",
    title: "License to Use",
    content: () => <>
      <p className="ta-body">
        <strong>{DEV_NAME}</strong> grants you a limited, non-exclusive, non-transferable,
        revocable license to use the App for personal, non-commercial purposes on devices you own
        or control.
      </p>
      <p className="ta-body">You may <strong>not</strong>:</p>
      <ul className="ta-list">
        <li>Copy, modify, or distribute the App or its content</li>
        <li>Reverse engineer, decompile, or disassemble the App</li>
        <li>Remove or alter any proprietary notices or labels</li>
        <li>Use the App for any commercial purpose without written permission</li>
        <li>Transfer your license to another person or "sell" access to the App</li>
        <li>Use automated tools, bots, or scripts to interact with the App</li>
      </ul>
    </>
  },
  {
    num: "03", emoji: "🎮", color: "rgba(6,214,160,0.1)",
    title: "Game Content & Virtual Items",
    content: () => <>
      <p className="ta-body">
        All game content — including levels, puzzles, graphics, sounds, animations, and the star
        economy — is owned by {DEV_NAME} and protected by applicable intellectual property laws.
      </p>
      <p className="ta-body">
        <strong>Stars</strong> (in-game currency) are virtual items with no real-world monetary value.
        They cannot be transferred, sold, or exchanged outside the App. Stars may be reset if you
        reinstall the App, as progress is stored locally on your device.
      </p>
      <div className="ta-callout blue">
        <span className="ta-callout-icon">ℹ️</span>
        <span>
          Tap Away is currently free with no in-app purchases. Stars are earned through gameplay only.
          No real money is required or accepted within the App.
        </span>
      </div>
    </>
  },
  {
    num: "04", emoji: "🌐", color: "rgba(255,159,28,0.1)",
    title: "Internet & Offline Use",
    content: () => <>
      <p className="ta-body">
        Tap Away is designed for <strong>offline play</strong>. An internet connection is only required to:
      </p>
      <ul className="ta-list">
        <li>Download the App initially from Google Play</li>
        <li>Receive app updates from the Play Store</li>
        <li>Load advertisements (if ads are enabled in the App)</li>
      </ul>
      <p className="ta-body">
        All puzzle gameplay, level progression, and daily challenges function fully without
        an internet connection.
      </p>
    </>
  },
  {
    num: "05", emoji: "🚫", color: "rgba(255,77,109,0.1)",
    title: "Prohibited Conduct",
    content: () => <>
      <p className="ta-body">When using the App, you agree not to:</p>
      <ul className="ta-list">
        <li>Cheat, exploit bugs, or use third-party software to manipulate game state</li>
        <li>Attempt to hack, overload, or interfere with the App's functionality</li>
        <li>Use the App in any way that violates applicable local, national, or international laws</li>
        <li>Impersonate any person or misrepresent your affiliation with any entity</li>
        <li>Collect or harvest any data from the App</li>
      </ul>
      <div className="ta-callout red">
        <span className="ta-callout-icon">⛔</span>
        <span>
          Violation of these terms may result in termination of your access to the App without notice.
        </span>
      </div>
    </>
  },
  {
    num: "06", emoji: "📢", color: "rgba(76,201,240,0.1)",
    title: "Advertisements",
    content: () => <>
      <p className="ta-body">
        The App may display advertisements provided by Google AdMob. By using the App,
        you consent to the display of such advertisements.
      </p>
      <p className="ta-body">
        We are not responsible for the content of third-party advertisements. Clicking on any
        advertisement is at your own risk. {DEV_NAME} does not endorse any products or services
        advertised within the App.
      </p>
      <p className="ta-body">
        Advertisement content is governed by Google's advertising policies. You may manage
        ad preferences through your device's Google settings.
      </p>
    </>
  },
  {
    num: "07", emoji: "⚖️", color: "rgba(162,155,254,0.1)",
    title: "Disclaimer of Warranties",
    content: () => <>
      <p className="ta-body">
        The App is provided on an <strong>"AS IS" and "AS AVAILABLE"</strong> basis without any
        warranty of any kind, express or implied, including but not limited to:
      </p>
      <ul className="ta-list">
        <li>Merchantability or fitness for a particular purpose</li>
        <li>Uninterrupted or error-free operation</li>
        <li>Accuracy or completeness of any content</li>
        <li>Freedom from viruses or other harmful components</li>
      </ul>
      <p className="ta-body">
        Your use of the App is entirely at your own risk.
      </p>
    </>
  },
  {
    num: "08", emoji: "🛡️", color: "rgba(6,214,160,0.1)",
    title: "Limitation of Liability",
    content: () => <>
      <p className="ta-body">
        To the maximum extent permitted by applicable law, <strong>{DEV_NAME}</strong> shall not be
        liable for any indirect, incidental, special, consequential, or punitive damages,
        including but not limited to:
      </p>
      <ul className="ta-list">
        <li>Loss of game progress due to device failure or App reinstallation</li>
        <li>Loss of stars or daily challenge streaks</li>
        <li>Interruption of service or App unavailability</li>
        <li>Any other loss or damage arising from your use of the App</li>
      </ul>
      <p className="ta-body">
        In no event shall our total liability exceed the amount you paid for the App (which is zero,
        as the App is free).
      </p>
    </>
  },
  {
    num: "09", emoji: "🔏", color: "rgba(255,159,28,0.1)",
    title: "Intellectual Property",
    content: () => <>
      <p className="ta-body">
        All content within {APP_NAME} — including but not limited to graphics, UI design, sound effects,
        background music, animations, puzzle layouts, and source code — is the exclusive intellectual
        property of <strong>{DEV_NAME}</strong> and is protected by copyright, trademark, and other laws.
      </p>
      <p className="ta-body">
        The app name "Tap Away", logo, and associated branding are owned by {DEV_NAME}.
        Unauthorized use, reproduction, or distribution of any app content is strictly prohibited.
      </p>
    </>
  },
  {
    num: "10", emoji: "📍", color: "rgba(76,201,240,0.1)",
    title: "Governing Law",
    content: () => <>
      <p className="ta-body">
        These Terms shall be governed by and construed in accordance with the laws of India,
        without regard to conflict of law principles.
      </p>
      <p className="ta-body">
        Any disputes arising from these Terms or your use of the App shall be subject to the
        exclusive jurisdiction of the courts located in India.
      </p>
    </>
  },
  {
    num: "11", emoji: "📧", color: "rgba(162,155,254,0.1)",
    title: "Contact & Support",
    content: () => <>
      <p className="ta-body">
        For questions about these Terms, bugs, feedback, or support requests,
        please contact us:
      </p>
      <ul className="ta-list">
        <li><strong>Developer:</strong> {DEV_NAME}</li>
        <li><strong>Email:</strong> <a href={`mailto:${DEV_EMAIL}`}>{DEV_EMAIL}</a></li>
        <li><strong>App:</strong> {APP_NAME}</li>
        <li><strong>Package:</strong> {PACKAGE}</li>
      </ul>
      <div className="ta-callout green">
        <span className="ta-callout-icon">💬</span>
        <span>
          We typically respond to all enquiries within 48 hours. Your feedback helps us make
          Tap Away better for everyone!
        </span>
      </div>
    </>
  },
];

// ── Main component ───────────────────────────────────────────────
export default function Privacytermsarrowtapaway() {
  const [activeTab, setActiveTab] = useState("privacy");

  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  // Scroll to top on tab change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const isPrivacy = activeTab === "privacy";
  const sections = isPrivacy ? PP_SECTIONS : TC_SECTIONS;

  return (
    <div className="ta-page">
      <div className="ta-bg" />
      <div className="ta-grid" />

      {/* Nav */}
      <nav className="ta-nav">
        <div className="ta-nav-inner">
          <a href="#" className="ta-nav-logo">
            <div className="ta-nav-icon">→</div>
            Tap Away
          </a>
          <div className="ta-nav-tabs">
            <button
              className={`ta-tab ${isPrivacy ? "active" : ""}`}
              onClick={() => setActiveTab("privacy")}
            >
              Privacy Policy
            </button>
            <button
              className={`ta-tab ${!isPrivacy ? "active" : ""}`}
              onClick={() => setActiveTab("terms")}
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </nav>

      <div className="ta-wrap">
        {/* Hero */}
        <header className="ta-hero" key={activeTab}>
          <div className="ta-hero-eyebrow">
            {isPrivacy ? "🔒 Legal" : "📜 Legal"} · {APP_NAME}
          </div>
          <h1>
            {isPrivacy ? (<><em>Privacy</em> Policy</>) : (<>Terms & <em>Conditions</em></>)}
          </h1>
          <p className="ta-hero-sub">
            {isPrivacy
              ? `How ${DEV_NAME} collects, uses, and protects information related to your use of ${APP_NAME}.`
              : `The rules and agreements that govern your use of ${APP_NAME} on Google Play.`
            }
          </p>
          <div className="ta-hero-meta">
            <span className="ta-meta-chip">
              <span className="ta-meta-dot" />
              Effective {EFFECTIVE_DATE}
            </span>
            <span className="ta-meta-chip">
              <span className="ta-meta-dot" style={{ background: "var(--accent2)" }} />
              Developer: {DEV_NAME}
            </span>
            <span className="ta-meta-chip">
              <span className="ta-meta-dot" style={{ background: "var(--green)" }} />
              {isPrivacy ? "No personal data collected" : "Free app · No in-app purchases"}
            </span>
          </div>
        </header>

        <div className="ta-divider" />

        {/* Section cards */}
        {sections.map((s, i) => (
          <div
            key={`${activeTab}-${s.num}`}
            className="ta-section"
            style={{ animationDelay: `${i * 0.04}s` }}
          >
            <div className="ta-section-head">
              <div className="ta-section-badge" style={{ background: s.color }}>
                {s.emoji}
              </div>
              <div>
                <div className="ta-section-num">Section {s.num}</div>
                <div className="ta-section-title">{s.title}</div>
              </div>
            </div>
            <s.content />
          </div>
        ))}

        {/* Contact card */}
        <div className="ta-contact">
          <h3>Have a question?</h3>
          <p>
            We're happy to help with anything related to {APP_NAME} or these legal documents.
            Reach out and we'll get back to you within 48 hours.
          </p>
          <a href={`mailto:${DEV_EMAIL}`} className="ta-contact-btn">
            ✉️ &nbsp;{DEV_EMAIL}
          </a>
        </div>

        {/* Footer */}
        <footer className="ta-footer">
          <div className="ta-divider" style={{ margin: "40px 0 24px" }} />
          <p>© 2026 <strong>{DEV_NAME}</strong> · {APP_NAME} · All rights reserved.</p>
          <p style={{ marginTop: 4 }}>
            This app is not affiliated with or endorsed by Google LLC.
          </p>
          <p style={{ marginTop: 4 }}>
            <button
              onClick={() => setActiveTab("privacy")}
              style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}
            >
              Privacy Policy
            </button>
            {" · "}
            <button
              onClick={() => setActiveTab("terms")}
              style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}
            >
              Terms & Conditions
            </button>
          </p>
        </footer>
      </div>
    </div>
  );
}